import React, { useEffect, useRef, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import { makeStyles } from '@material-ui/core/styles';
import { FormHelperText, IconButton, InputAdornment, Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';

export const customInputUseStyles = makeStyles((theme) => ({
  formControl: {
    position: 'relative',
    width: '100%',
    margin: '0px 0px 15px 0px',
  },
  infoIcon: {
    position: 'absolute',
    top: '-17px',
    // top: '-25px', // Changed to position tooltip above the icon
    left: '146px',
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  },
  inputLabel: {
    position: 'absolute',
    left: 0,
    top: -30,
    color: '#1D3557',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  centerAlignLabel: {
    width: '100%',
    textAlign: 'center',
    color: '#1D3557',
    fontWeight: 700,
  },
  textField: {
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-input': {
      padding: '12.5px 12px',
    },
  },
  maxWidth: {
    maxWidth: '245px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'unset',
    },
  },
  centeredContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '498px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  centeredContentForTools: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  otpTextField: {
    '& input': {
      // height: "19.422px",
      width: '400px',
      textAlign: 'center',
      paddingLeft: theme.spacing(4),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  },
  otpTextFieldForTools: {
    '& input': {
      // height: "19.422px",
      minWidth: '230px',
      textAlign: 'center',
      paddingLeft: theme.spacing(4),
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
      [theme.breakpoints.up(450)]: {
        minWidth: '450px',
      },
    },
  },
  selectFormControl: {
    width: '100%',
  },
  staticLabel: {
    top: '-27px',
    left: '-10px',
  },
  selectEmpty: {
    marginTop: '15px',
    textAlign: 'left',
    '& .MuiOutlinedInput-input': {
      padding: '12.5px 12px',
    },
  },
  placeholder: {
    color: '#979797',
    fontFamily: 'inherit',
    fontSize: '13.942px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24.398px',
    float: 'left',
  },
}));

// Updated component to make it reusable
const CustomInput = ({
  label,
  id = 'work-email',
  type = 'text',
  onChange,
  error = false,
  errorMessage = '',
  isOtp = false,
  isOnToolsVerify = false,
  ...props
}) => {
  const classes = customInputUseStyles();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipRef = useRef<HTMLButtonElement | null>(null);

  const handleTooltipToggle = () => {
    setTooltipOpen(!tooltipOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setTooltipOpen(false); // Close tooltip if click is outside
      }
    };

    // Add when the tooltip is open and remove when it is closed
    if (props.info && tooltipOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tooltipOpen]);

  const inputProps = isOtp
    ? {
        inputProps: {
          maxLength: 6,
          inputMode: 'numeric' as const,
          pattern: '[0-9]*',
        },
        className: `${classes.textField} ${isOnToolsVerify ? classes.otpTextFieldForTools : classes.otpTextField} ${
          props.whiteBg ? 'bg-white' : ''
        }`,
      }
    : {};

  return (
    <FormControl
      error={error}
      className={`${classes.formControl} ${props.limitWidth ? classes.maxWidth : ''} ${
        isOtp && !isOnToolsVerify ? classes.centeredContent : isOnToolsVerify ? classes.centeredContentForTools : ''
      } ${props.fullWidth ? 'w-100' : ''}`}
    >
      <InputLabel htmlFor={id} className={`${classes.inputLabel} ${isOtp ? classes.centerAlignLabel : ''}`}>
        {label}
      </InputLabel>
      {props.info && (
        <Tooltip title={props.info} placement="top" open={tooltipOpen} classes={{ tooltip: 'm-0' }} arrow>
          <IconButton ref={tooltipRef} className={classes.infoIcon} onClick={handleTooltipToggle}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      )}
      {!isOnToolsVerify ? (
        <TextField
          id={id}
          className={`${classes.textField} ${props.whiteBg ? 'bg-white' : ''}`}
          variant="outlined"
          onChange={onChange}
          type={isOtp ? 'tel' : type}
          InputLabelProps={{ shrink: true }}
          // InputProps={{
          //     startAdornment: <InputAdornment position="start">https://</InputAdornment>,
          //   }}
          error={error}
          {...inputProps}
          {...props} // Spread additional props to TextField
        />
      ) : (
        <TextField
          id={id}
          className={`${classes.textField} ${props.whiteBg ? 'bg-white' : ''}`}
          variant="outlined"
          onChange={onChange}
          type={isOtp ? 'tel' : type}
          // InputLabelProps={{ shrink: true }}
          // InputProps={{
          //     startAdornment: <InputAdornment position="start">https://</InputAdornment>,
          //   }}
          error={error}
          {...inputProps}
          {...props} // Spread additional props to TextField
        />
      )}

      {error && <FormHelperText id={`${id}-error-text`}>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default CustomInput;
